// Function index:
//   renderSystem(host, ctx) — gm_jobs counts, table sizes, Anthropic
//       spend (today + 7-day rolling), recent failures with per-row
//       retry + "Retry all failed" bulk action.

import {
  getAdminSystem, retryAdminJob, retryAllFailedAdminJobs,
  type AdminSystem,
} from '../../api.js';
import { type AdminPanelCtx } from './helpers.js';

export async function renderSystem(host: HTMLElement, ctx: AdminPanelCtx): Promise<void> {
  host.innerHTML = 'Loading…';
  try {
    const s = await getAdminSystem();
    host.innerHTML = '';
    host.appendChild(systemBlock(s, host, ctx));
  } catch (err) {
    host.innerHTML = '';
    ctx.setError(`system: ${(err as Error).message}`);
  }
}

function systemBlock(s: AdminSystem, host: HTMLElement, ctx: AdminPanelCtx): HTMLDivElement {
  const wrap = document.createElement('div');
  const jobs = s.jobs;
  const lines: string[] = [];
  lines.push(`<div><strong>gm_jobs:</strong> pending ${jobs.pending} · running ${jobs.running} · done ${jobs.done} · failed <span style="color:${jobs.failed > 0 ? '#ff8888' : '#888'}">${jobs.failed}</span></div>`);
  lines.push(`<div style="margin-top:4px;"><strong>counts:</strong> users ${s.counts.users} · parties ${s.counts.parties} · messages ${s.counts.messages} · embeddings ${s.counts.embeddings}</div>`);
  lines.push(`<div><strong>memory:</strong> arc_events ${s.counts.arc_events} · arc_summaries ${s.counts.arc_summaries} · consequences ${s.counts.consequences}</div>`);

  const a = s.anthropic;
  const spend = a.spend_today_usd;
  const cap = a.cap_usd;
  const overCap = cap !== null && spend >= cap;
  const capLabel = cap === null ? 'no cap' : `cap $${cap.toFixed(2)}`;
  const spendColor = overCap ? '#ff8888' : cap !== null && spend > cap * 0.8 ? '#ffeb88' : '#aaa';
  lines.push(`<div style="margin-top:10px;"><strong>anthropic today:</strong> <span style="color:${spendColor}">$${spend.toFixed(4)}</span> · ${capLabel}${overCap ? ' · <span style="color:#ff8888">GM offline until UTC rollover</span>' : ''}</div>`);
  if (a.recent.length > 0) {
    const recent = a.recent
      .map((r) => `${r.date} $${r.spend_usd.toFixed(2)} (${r.requests} req)`)
      .join(' · ');
    lines.push(`<div class="party-meta" style="margin-left:8px;">7d: ${recent}</div>`);
  }

  wrap.innerHTML = lines.join('');

  if (s.recent_failures.length > 0) {
    const header = document.createElement('div');
    header.style.cssText = 'margin-top:10px; color:#ff8888; display:flex; align-items:center; gap:8px;';
    const title = document.createElement('strong');
    title.textContent = `Recent failures (${s.recent_failures.length}):`;
    header.appendChild(title);

    const retryAll = document.createElement('button');
    retryAll.type = 'button';
    retryAll.textContent = 'Retry all failed';
    retryAll.style.cssText = 'font-size:11px; padding:3px 8px;';
    retryAll.addEventListener('click', async () => {
      try {
        const r = await retryAllFailedAdminJobs();
        ctx.setInfo(`Reset ${r.updated} failed job(s) to pending.`);
        await renderSystem(host, ctx);
      } catch (err) {
        ctx.setError((err as Error).message);
      }
    });
    header.appendChild(retryAll);
    wrap.appendChild(header);

    for (const f of s.recent_failures) {
      const row = document.createElement('div');
      row.className = 'party-meta';
      row.style.cssText = 'margin-left:8px; display:flex; align-items:center; gap:6px;';
      const text = document.createElement('span');
      text.style.flex = '1';
      text.textContent = `#${f.id} party ${f.party_id} · ${f.kind} · attempts ${f.attempts}${f.last_error ? ' · ' + f.last_error : ''}`;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = 'Retry';
      btn.style.cssText = 'font-size:11px; padding:2px 6px;';
      btn.addEventListener('click', async () => {
        try {
          await retryAdminJob(f.id);
          await renderSystem(host, ctx);
        } catch (err) {
          ctx.setError((err as Error).message);
        }
      });
      row.appendChild(text);
      row.appendChild(btn);
      wrap.appendChild(row);
    }
  }
  return wrap;
}
